import { motion } from "framer-motion";
import HeartCanvas from "./HeartCanvas";
import MatchCard from "./MatchCard";
import { HeartScribble } from "./Logo";

const matches = [
    {
        name: "Moonchild",
        age: 23,
        match: 92,
        tags: ["Stargazer", "Yoga", "Tea"],
        handwritten: "Moonchild, 23 ✦",
        note: "looking for someone who reads books past midnight",
        image: "/mockup_profile.png",
        rotate: "-7deg",
        tape: "left",
        pos: "left-[4%] top-24 z-10",
    },
    {
        name: "Dreamcatcher",
        age: 24,
        match: 98,
        tags: ["Art", "Coffee", "Hike"],
        handwritten: "Dreamcatcher, 24",
        note: "98% compatible. unreal.",
        image: "/mockup_profile_2.png",
        rotate: "4deg",
        tape: "right",
        pos: "left-[34%] top-4 z-30",
    },
    {
        name: "NightOwl",
        age: 26,
        match: 97,
        tags: ["Rock", "Games", "Pizza"],
        handwritten: "NightOwl, 26 ♣",
        note: "messaged me first. winning already.",
        image: "/mockup_profile_3.png",
        rotate: "8deg",
        tape: "left",
        pos: "left-[62%] top-32 z-20",
    },
];

export default function Hero() {
    return (
        <section
            id="hero"
            className="relative min-h-screen pt-28 pb-16 lg:pt-36 overflow-hidden"
            data-testid="hero-section"
        >
            <div className="absolute inset-0 opacity-80">
                <HeartCanvas />
            </div>

            {/* Editorial top-strip: issue No., date, dingbats */}
            <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
                <div className="reveal in flex items-center justify-between flex-wrap gap-3 pb-6 border-b-2 border-[var(--ink)] font-mono text-[10px] uppercase tracking-[0.25em]">
                    <span>Vol. 01 · Issue 001</span>
                    <span className="hidden sm:inline">
                        ❦ The slow love quarterly ❦
                    </span>
                    <span>Alpha · Winter 2026</span>
                </div>
            </div>

            <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 mt-10 lg:mt-14 grid lg:grid-cols-[1.15fr_1fr] gap-12">
                {/* Left: massive editorial copy */}
                <div className="relative z-50">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="flex items-center gap-3 mb-7"
                        data-testid="hero-eyebrow"
                    >
                        <div className="stamp stamp-large">
                            Personals · est. 2026
                        </div>
                        <div className="font-hand text-xl text-[var(--hot)]">
                            anti-app dating ↘
                        </div>
                    </motion.div>

                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.1 }}
                        className="font-display text-[14vw] sm:text-[10vw] lg:text-[9.2rem] xl:text-[10.5rem] leading-[0.84] tracking-[-0.04em]"
                        data-testid="hero-headline"
                        style={{
                            fontVariationSettings: '"opsz" 144, "SOFT" 100, "WONK" 1',
                        }}
                    >
                        Stop{" "}
                        <span className="font-italic-serif text-[var(--hot)]">
                            swiping.
                        </span>
                        <br />
                        <span className="relative inline-block">
                            Start{" "}
                            <span className="scribble-circle relative z-50 font-italic-serif text-[var(--hot)]">
                                meaning&nbsp;it.
                            </span>
                        </span>
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.3 }}
                        className="mt-10 max-w-xl text-lg leading-relaxed text-[var(--ink-soft)] font-body"
                    >
                        <span className="font-display text-2xl text-[var(--ink)]">
                            LÜVYN
                        </span>{" "}
                        is the anti-ghosting dating app. Five carefully chosen
                        humans, hand-picked just for you, every Monday. No
                        endless swiping. No algorithmic black holes. Just real
                        connection, the slow way.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.45 }}
                        className="mt-10 flex flex-wrap items-center gap-4"
                    >
                        <a
                            href="#waitlist"
                            className="hot-button inline-flex items-center gap-3 px-7 py-4 text-sm font-mono uppercase tracking-[0.18em] border-2 border-[var(--ink)]"
                            data-testid="hero-cta-primary"
                        >
                            Get on the list
                            <HeartScribble className="w-4 h-4" color="currentColor" />
                        </a>
                        <a
                            href="#how"
                            className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-[0.18em] text-[var(--ink)] hover:text-[var(--hot)] border-b-2 border-[var(--ink)] hover:border-[var(--hot)] pb-1.5 transition-all"
                            data-testid="hero-cta-secondary"
                        >
                            Read the manifesto ↘
                        </a>
                    </motion.div>

                    {/* Stats strip */}
                    <div className="mt-14 grid grid-cols-3 gap-3 max-w-xl border-t-2 border-[var(--ink)] pt-6">
                        {[
                            { v: "5", l: "matches / week" },
                            { v: "12", l: "compatibility dims" },
                            { v: "0", l: "endless swipes" },
                        ].map((s) => (
                            <div key={s.l}>
                                <div className="font-display text-5xl text-[var(--ink)]">
                                    {s.v}
                                </div>
                                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--ink-soft)] mt-2">
                                    {s.l}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Right: polaroid wall */}
                <div className="relative z-20 h-[560px] lg:h-[680px] hidden md:block overflow-visible">
                    {/* hand-drawn arrow */}
                    <svg
                        className="absolute -top-4 left-2 text-[var(--hot)] hidden lg:block"
                        width="140"
                        height="80"
                        viewBox="0 0 140 80"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                    >
                        <path d="M5 70 C 40 30, 80 10, 130 22" />
                        <path d="M122 12 L 132 22 L 122 32" />
                    </svg>
                    <div className="absolute -top-6 -left-2 font-hand text-xl text-[var(--hot)] -rotate-6 hidden lg:block">
                        this week's matches →
                    </div>

                    {matches.map((m, i) => (
                        <motion.div
                            key={m.name}
                            initial={{ opacity: 0, y: 40, rotate: 0 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                                duration: 1,
                                delay: 0.4 + i * 0.15,
                            }}
                            className={`absolute floaty ${m.pos}`}
                            style={{ "--r": m.rotate, animationDelay: `${i * 0.7}s` }}
                        >
                            <MatchCard
                                {...m}
                                className="w-[10.75rem] lg:w-[11.5rem] xl:w-[12.5rem]"
                                testid={`match-card-${m.name.toLowerCase()}`}
                            />
                        </motion.div>
                    ))}
                </div>
            </div>

            {/* bottom ticker scroll cue */}
            <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 mt-16 flex items-end justify-between font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ink-soft)]">
                <span>scroll ↓ the slow love begins</span>
                <span className="hidden md:inline">verified humans only</span>
            </div>
        </section>
    );
}
